import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 */
class ProductPrice extends HTMLElement {
  connectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  disconnectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  /**
   * Updates the price and volume pricing note.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (
      event.target instanceof HTMLElement &&
      event.target.dataset.productId !== this.dataset.productId
    ) {
      return;
    }

    const newProductPrice = event.detail.data.html.querySelector(
      `product-price[data-block-id="${this.dataset.blockId}"]`
    );

    if (!newProductPrice) return;

    const newPrice = newProductPrice.querySelector('[ref="priceContainer"]');
    const currentPrice = this.querySelector('[ref="priceContainer"]');

    if (newPrice && currentPrice) {
      currentPrice.replaceWith(newPrice);
    }

    const currentNote = this.querySelector('.volume-pricing-note');
    const newNote = newProductPrice.querySelector('.volume-pricing-note');

    if (!newNote) {
      currentNote?.remove();
    } else if (!currentNote) {
      this.querySelector('[ref="priceContainer"]')?.insertAdjacentElement(
        'afterend',
        /** @type {Element} */ (newNote.cloneNode(true))
      );
    } else {
      currentNote.replaceWith(newNote);
    }
  };
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}

/* -------------------------------------------------------------------------- */
/* HBA LOCAL-CURRENCY STOREFRONT DISPLAY                                      */
/* -------------------------------------------------------------------------- */

const HBA_DOMESTIC_COUNTRY = 'EG';
const HBA_BASE_CURRENCY = 'EGP';

const HBA_FX_CACHE_KEY = 'hba-egp-fx-rates-v3';
const HBA_FX_CACHE_TTL = 24 * 60 * 60 * 1000;

const HBA_ESTIMATE_SELECTOR = '[data-hba-currency-estimate]';

const HBA_FX_ENDPOINTS = [
  'https://cdn.jsdelivr.net/npm/@fawazahmed0/currency-api@latest/v1/currencies/egp.min.json',
  'https://latest.currency-api.pages.dev/v1/currencies/egp.min.json',
];

const HBA_COUNTRY_CURRENCY = {
  AD: 'EUR',
  AE: 'AED',
  AF: 'AFN',
  AG: 'XCD',
  AI: 'XCD',
  AL: 'ALL',
  AM: 'AMD',
  AO: 'AOA',
  AR: 'ARS',
  AS: 'USD',
  AT: 'EUR',
  AU: 'AUD',
  AW: 'AWG',
  AX: 'EUR',
  AZ: 'AZN',

  BA: 'BAM',
  BB: 'BBD',
  BD: 'BDT',
  BE: 'EUR',
  BF: 'XOF',
  BG: 'BGN',
  BH: 'BHD',
  BI: 'BIF',
  BJ: 'XOF',
  BL: 'EUR',
  BM: 'BMD',
  BN: 'BND',
  BO: 'BOB',
  BQ: 'USD',
  BR: 'BRL',
  BS: 'BSD',
  BT: 'INR',
  BV: 'NOK',
  BW: 'BWP',
  BY: 'BYN',
  BZ: 'BZD',

  CA: 'CAD',
  CC: 'AUD',
  CD: 'CDF',
  CF: 'XAF',
  CG: 'XAF',
  CH: 'CHF',
  CI: 'XOF',
  CK: 'NZD',
  CL: 'CLP',
  CM: 'XAF',
  CN: 'CNY',
  CO: 'COP',
  CR: 'CRC',
  CU: 'CUP',
  CV: 'CVE',
  CW: 'XCG',
  CX: 'AUD',
  CY: 'EUR',
  CZ: 'CZK',

  DE: 'EUR',
  DJ: 'DJF',
  DK: 'DKK',
  DM: 'XCD',
  DO: 'DOP',
  DZ: 'DZD',

  EC: 'USD',
  EE: 'EUR',
  EG: 'EGP',
  EH: 'MAD',
  ER: 'ERN',
  ES: 'EUR',
  ET: 'ETB',

  FI: 'EUR',
  FJ: 'FJD',
  FK: 'FKP',
  FM: 'USD',
  FO: 'DKK',
  FR: 'EUR',

  GA: 'XAF',
  GB: 'GBP',
  GD: 'XCD',
  GE: 'GEL',
  GF: 'EUR',
  GG: 'GBP',
  GH: 'GHS',
  GI: 'GIP',
  GL: 'DKK',
  GM: 'GMD',
  GN: 'GNF',
  GP: 'EUR',
  GQ: 'XAF',
  GR: 'EUR',
  GS: 'GBP',
  GT: 'GTQ',
  GU: 'USD',
  GW: 'XOF',
  GY: 'GYD',

  HK: 'HKD',
  HM: 'AUD',
  HN: 'HNL',
  HR: 'EUR',
  HT: 'HTG',
  HU: 'HUF',

  ID: 'IDR',
  IE: 'EUR',
  IL: 'ILS',
  IM: 'GBP',
  IN: 'INR',
  IO: 'USD',
  IQ: 'IQD',
  IR: 'IRR',
  IS: 'ISK',
  IT: 'EUR',

  JE: 'GBP',
  JM: 'JMD',
  JO: 'JOD',
  JP: 'JPY',

  KE: 'KES',
  KG: 'KGS',
  KH: 'KHR',
  KI: 'AUD',
  KM: 'KMF',
  KN: 'XCD',
  KP: 'KPW',
  KR: 'KRW',
  KW: 'KWD',
  KY: 'KYD',
  KZ: 'KZT',

  LA: 'LAK',
  LB: 'LBP',
  LC: 'XCD',
  LI: 'CHF',
  LK: 'LKR',
  LR: 'LRD',
  LS: 'ZAR',
  LT: 'EUR',
  LU: 'EUR',
  LV: 'EUR',
  LY: 'LYD',

  MA: 'MAD',
  MC: 'EUR',
  MD: 'MDL',
  ME: 'EUR',
  MF: 'EUR',
  MG: 'MGA',
  MH: 'USD',
  MK: 'MKD',
  ML: 'XOF',
  MM: 'MMK',
  MN: 'MNT',
  MO: 'MOP',
  MP: 'USD',
  MQ: 'EUR',
  MR: 'MRU',
  MS: 'XCD',
  MT: 'EUR',
  MU: 'MUR',
  MV: 'MVR',
  MW: 'MWK',
  MX: 'MXN',
  MY: 'MYR',
  MZ: 'MZN',

  NA: 'ZAR',
  NC: 'XPF',
  NE: 'XOF',
  NF: 'AUD',
  NG: 'NGN',
  NI: 'NIO',
  NL: 'EUR',
  NO: 'NOK',
  NP: 'NPR',
  NR: 'AUD',
  NU: 'NZD',
  NZ: 'NZD',

  OM: 'OMR',

  PA: 'PAB',
  PE: 'PEN',
  PF: 'XPF',
  PG: 'PGK',
  PH: 'PHP',
  PK: 'PKR',
  PL: 'PLN',
  PM: 'EUR',
  PN: 'NZD',
  PR: 'USD',
  PS: 'ILS',
  PT: 'EUR',
  PW: 'USD',
  PY: 'PYG',

  QA: 'QAR',

  RE: 'EUR',
  RO: 'RON',
  RS: 'RSD',
  RU: 'RUB',
  RW: 'RWF',

  SA: 'SAR',
  SB: 'SBD',
  SC: 'SCR',
  SD: 'SDG',
  SE: 'SEK',
  SG: 'SGD',
  SH: 'SHP',
  SI: 'EUR',
  SJ: 'NOK',
  SK: 'EUR',
  SL: 'SLE',
  SM: 'EUR',
  SN: 'XOF',
  SO: 'SOS',
  SR: 'SRD',
  SS: 'SSP',
  ST: 'STN',
  SV: 'USD',
  SX: 'XCG',
  SY: 'SYP',
  SZ: 'SZL',

  TC: 'USD',
  TD: 'XAF',
  TF: 'EUR',
  TG: 'XOF',
  TH: 'THB',
  TJ: 'TJS',
  TK: 'NZD',
  TL: 'USD',
  TM: 'TMT',
  TN: 'TND',
  TO: 'TOP',
  TR: 'TRY',
  TT: 'TTD',
  TV: 'AUD',
  TW: 'TWD',
  TZ: 'TZS',

  UA: 'UAH',
  UG: 'UGX',
  UM: 'USD',
  US: 'USD',
  UY: 'UYU',
  UZ: 'UZS',

  VA: 'EUR',
  VC: 'XCD',
  VE: 'VES',
  VG: 'USD',
  VI: 'USD',
  VN: 'VND',
  VU: 'VUV',

  WF: 'XPF',
  WS: 'WST',

  XK: 'EUR',

  YE: 'YER',
  YT: 'EUR',

  ZA: 'ZAR',
  ZM: 'ZMW',
  ZW: 'ZWG',
};

/** @type {Promise<Record<string, number> | null> | null} */
let hbaRatesPromise = null;

/* -------------------------------------------------------------------------- */
/* EXCHANGE RATES                                                             */
/* -------------------------------------------------------------------------- */

function hbaReadCachedRates() {
  try {
    const cached = localStorage.getItem(HBA_FX_CACHE_KEY);

    if (!cached) {
      return null;
    }

    const parsed = JSON.parse(cached);

    if (
      !parsed ||
      typeof parsed.savedAt !== 'number' ||
      typeof parsed.rates !== 'object'
    ) {
      return null;
    }

    if (Date.now() - parsed.savedAt > HBA_FX_CACHE_TTL) {
      return null;
    }

    return parsed.rates;
  } catch (_error) {
    return null;
  }
}

function hbaWriteCachedRates(rates) {
  try {
    localStorage.setItem(
      HBA_FX_CACHE_KEY,
      JSON.stringify({
        savedAt: Date.now(),
        rates,
      })
    );
  } catch (_error) {
    // Store still works if browser storage is unavailable.
  }
}

async function hbaFetchRates() {
  const cachedRates = hbaReadCachedRates();

  if (cachedRates) {
    return cachedRates;
  }

  for (const endpoint of HBA_FX_ENDPOINTS) {
    try {
      const response = await fetch(endpoint, {
        method: 'GET',
        mode: 'cors',
        credentials: 'omit',
        cache: 'force-cache',
      });

      if (!response.ok) {
        continue;
      }

      const payload = await response.json();

      const rates = payload?.egp;

      if (!rates || typeof rates !== 'object') {
        continue;
      }

      hbaWriteCachedRates(rates);

      return rates;
    } catch (_error) {
      // Try the second exchange-rate endpoint.
    }
  }

  return null;
}

function hbaGetRates() {
  if (!hbaRatesPromise) {
    hbaRatesPromise = hbaFetchRates();
  }

  return hbaRatesPromise;
}

/* -------------------------------------------------------------------------- */
/* COUNTRY -> DISPLAY CURRENCY                                                */
/* -------------------------------------------------------------------------- */

function hbaResolveTargetCurrency(
  country,
  configuredCurrency
) {
  const configured = (
    configuredCurrency || ''
  ).toUpperCase();

  if (country === HBA_DOMESTIC_COUNTRY) {
    return HBA_BASE_CURRENCY;
  }

  /*
   * If Shopify genuinely provides a non-EGP currency,
   * respect Shopify first.
   */
  if (
    configured &&
    configured !== HBA_BASE_CURRENCY
  ) {
    return configured;
  }

  /*
   * Otherwise use the normal currency of the
   * selected country for the storefront estimate.
   */
  return HBA_COUNTRY_CURRENCY[country] || '';
}

/* -------------------------------------------------------------------------- */
/* FORMATTING                                                                 */
/* -------------------------------------------------------------------------- */

function hbaFormatCurrency(
  value,
  currencyCode
) {
  try {
    return new Intl.NumberFormat(
      document.documentElement.lang || 'en',
      {
        style: 'currency',
        currency: currencyCode,
        currencyDisplay: 'narrowSymbol',
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }
    ).format(value);
  } catch (_error) {
    return `${Number(value).toFixed(2)} ${currencyCode}`;
  }
}

function hbaBuildConvertedText({
  amountCents,
  minCents,
  maxCents,
  rate,
  currencyCode,
}) {
  const hasRange =
    Number.isFinite(minCents) &&
    Number.isFinite(maxCents) &&
    minCents > 0 &&
    maxCents > 0 &&
    minCents !== maxCents;

  if (hasRange) {
    const convertedMin =
      (minCents / 100) * rate;

    const convertedMax =
      (maxCents / 100) * rate;

    return (
      `${hbaFormatCurrency(
        convertedMin,
        currencyCode
      )} – ` +
      `${hbaFormatCurrency(
        convertedMax,
        currencyCode
      )}`
    );
  }

  return hbaFormatCurrency(
    (amountCents / 100) * rate,
    currencyCode
  );
}

/* -------------------------------------------------------------------------- */
/* PRODUCT / PRODUCT CARD PRICES                                              */
/* -------------------------------------------------------------------------- */

function hbaApplyProductPrice(
  element,
  convertedText
) {
  const priceContainer =
    element.closest('[ref="priceContainer"]');

  if (!priceContainer) {
    return false;
  }

  const visiblePrice =
    priceContainer.querySelector('.price');

  if (!visiblePrice) {
    return false;
  }

  /*
   * Replace the visible EGP price itself.
   *
   * Example:
   *
   * E£2,000
   *
   * becomes:
   *
   * SAR 150
   *
   * There is NO second currency line.
   */
  visiblePrice.textContent = convertedText;

  /*
   * Don't leave an EGP compare-at price beside
   * a local-currency sale price.
   */
  priceContainer
    .querySelectorAll('.compare-at-price')
    .forEach((node) => {
      node.hidden = true;
    });

  /*
   * Unit price is separately rendered in EGP.
   * Hide it internationally rather than mixing currencies.
   */
  priceContainer
    .querySelectorAll('.unit-price')
    .forEach((node) => {
      node.hidden = true;
    });

  /*
   * The helper element from price.liquid remains
   * available for data, but isn't visually shown.
   */
  element.hidden = true;
  element.textContent = '';

  return true;
}

/* -------------------------------------------------------------------------- */
/* CART TOTAL                                                                 */
/* -------------------------------------------------------------------------- */

function hbaApplyCartTotal(
  element,
  convertedText
) {
  const totalContainer =
    element.closest('.cart__total-container');

  if (!totalContainer) {
    return false;
  }

  const cartTotal =
    totalContainer.querySelector('[ref="cartTotal"]');

  if (!cartTotal) {
    return false;
  }

  /*
   * Replace EGP cart total with local currency.
   */
  cartTotal.textContent = convertedText;

  cartTotal.setAttribute(
    'value',
    convertedText
  );

  /*
   * File 3 previously created a second:
   *
   * Approx. local total
   *
   * row.
   *
   * We no longer need it because the normal cart
   * total itself now displays local currency.
   */
  const estimateRow =
    element.closest(
      '.hba-cart-local-estimate-row'
    );

  if (estimateRow) {
    estimateRow.hidden = true;
  }

  /*
   * If cart-level discounts exist, Horizon's
   * auxiliary subtotal/discount block contains EGP.
   *
   * Hide it so we don't mix currencies.
   */
  const summaryTotals =
    totalContainer.closest(
      '.cart__summary-totals'
    );

  summaryTotals
    ?.querySelector(
      '.cart__original-total-container'
    )
    ?.setAttribute(
      'hidden',
      ''
    );

  element.hidden = true;
  element.textContent = '';

  return true;
}

function hbaApplyConvertedPrice(
  element,
  convertedText
) {
  if (
    element.closest(
      '.hba-cart-local-estimate-row'
    )
  ) {
    return hbaApplyCartTotal(
      element,
      convertedText
    );
  }

  return hbaApplyProductPrice(
    element,
    convertedText
  );
}

/* -------------------------------------------------------------------------- */
/* CONVERSION                                                                 */
/* -------------------------------------------------------------------------- */

async function hbaRenderLocalPrice(
  element
) {
  if (!(element instanceof HTMLElement)) {
    return;
  }

  const country = (
    element.dataset.hbaCountry || ''
  ).toUpperCase();

  const sourceCurrency = (
    element.dataset.hbaSourceCurrency || ''
  ).toUpperCase();

  const configuredTargetCurrency = (
    element.dataset.hbaTargetCurrency || ''
  ).toUpperCase();

  const targetCurrency =
    hbaResolveTargetCurrency(
      country,
      configuredTargetCurrency
    );

  const amountCents = Number(
    element.dataset.hbaAmountCents || '0'
  );

  const minCents = Number(
    element.dataset.hbaMinCents || '0'
  );

  const maxCents = Number(
    element.dataset.hbaMaxCents || '0'
  );

  /*
   * Egypt:
   * keep normal EGP.
   *
   * International:
   * convert only if the actual Shopify price
   * we're receiving is EGP.
   */
  if (
    !country ||
    country === HBA_DOMESTIC_COUNTRY ||
    sourceCurrency !== HBA_BASE_CURRENCY ||
    !targetCurrency ||
    targetCurrency === HBA_BASE_CURRENCY ||
    !Number.isFinite(amountCents) ||
    amountCents <= 0
  ) {
    element.hidden = true;
    element.textContent = '';

    return;
  }

  const signature = [
    country,
    sourceCurrency,
    targetCurrency,
    amountCents,
    minCents,
    maxCents,
  ].join('|');

  if (
    element.dataset.hbaRenderedSignature ===
    signature
  ) {
    return;
  }

  const rates = await hbaGetRates();

  /*
   * Important fallback:
   *
   * If conversion can't load, we DO NOT hide the
   * real Shopify EGP price.
   *
   * This prevents products appearing with no price.
   */
  if (!rates) {
    return;
  }

  const rate = Number(
    rates[targetCurrency.toLowerCase()]
  );

  if (
    !Number.isFinite(rate) ||
    rate <= 0
  ) {
    return;
  }

  const convertedText =
    hbaBuildConvertedText({
      amountCents,
      minCents,
      maxCents,
      rate,
      currencyCode: targetCurrency,
    });

  if (
    hbaApplyConvertedPrice(
      element,
      convertedText
    )
  ) {
    element.dataset.hbaRenderedSignature =
      signature;
  }
}

/* -------------------------------------------------------------------------- */
/* DYNAMIC HORIZON UPDATES                                                    */
/* -------------------------------------------------------------------------- */

function hbaScanLocalPrices(
  root = document
) {
  if (
    root instanceof Element &&
    root.matches(HBA_ESTIMATE_SELECTOR)
  ) {
    hbaRenderLocalPrice(root);
  }

  if ('querySelectorAll' in root) {
    root
      .querySelectorAll(
        HBA_ESTIMATE_SELECTOR
      )
      .forEach((element) => {
        hbaRenderLocalPrice(element);
      });
  }
}

function hbaStartLocalPrices() {
  hbaScanLocalPrices(document);

  /*
   * Horizon dynamically swaps price HTML when:
   *
   * - variants change
   * - product cards change
   * - quick add changes
   * - cart updates
   *
   * Re-run our conversion whenever new price HTML
   * is inserted.
   */
  const observer =
    new MutationObserver(
      (mutations) => {
        for (
          const mutation of mutations
        ) {
          if (
            mutation.type ===
            'attributes'
          ) {
            hbaRenderLocalPrice(
              mutation.target
            );

            continue;
          }

          for (
            const node of
            mutation.addedNodes
          ) {
            if (
              node instanceof Element
            ) {
              hbaScanLocalPrices(node);
            }
          }
        }
      }
    );

  observer.observe(
    document.body,
    {
      subtree: true,
      childList: true,
      attributes: true,

      attributeFilter: [
        'data-hba-country',
        'data-hba-source-currency',
        'data-hba-target-currency',
        'data-hba-amount-cents',
        'data-hba-min-cents',
        'data-hba-max-cents',
      ],
    }
  );

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      if (
        event.target instanceof Element
      ) {
        hbaScanLocalPrices(
          event.target
        );
      }
    }
  );
}

/* -------------------------------------------------------------------------- */
/* START                                                                      */
/* -------------------------------------------------------------------------- */

if (
  document.readyState === 'loading'
) {
  document.addEventListener(
    'DOMContentLoaded',
    hbaStartLocalPrices,
    {
      once: true,
    }
  );
} else {
  hbaStartLocalPrices();
}

/* -------------------------------------------------------------------------- */
/* HBA: LOCALIZATION SELECTOR CURRENCY LABELS                                 */
/* -------------------------------------------------------------------------- */

function hbaGetSelectedCountryCode() {
  /*
   * Best source: our existing HBA price hook.
   * This is rendered by Shopify Liquid using localization.country.iso_code,
   * so it remains reliable even when Horizon rebuilds the header.
   */
  const currencyHook = document.querySelector(
    '[data-hba-currency-estimate][data-hba-country]'
  );

  const hookCountry = (
    currencyHook?.dataset.hbaCountry || ''
  ).toUpperCase();

  if (hookCountry) {
    return hookCountry;
  }

  /*
   * Fallback: currently selected country in Shopify's localization list.
   */
  const activeCountry = document.querySelector(
    '.localization-form__list-item[aria-current="true"][data-value]'
  );

  return (
    activeCountry?.dataset.value || ''
  ).toUpperCase();
}

function hbaGetDisplayCurrencyForCountry(countryCode) {
  const country = (
    countryCode || ''
  ).toUpperCase();

  if (!country) {
    return '';
  }

  if (country === HBA_DOMESTIC_COUNTRY) {
    return HBA_BASE_CURRENCY;
  }

  return HBA_COUNTRY_CURRENCY[country] || '';
}

function hbaUpdateLocalizationCurrencyLabels() {
  const selectedCountry =
    hbaGetSelectedCountryCode();

  const selectedCurrency =
    hbaGetDisplayCurrencyForCountry(
      selectedCountry
    );

  if (!selectedCurrency) {
    return;
  }

  /*
   * Desktop header button.
   *
   * Example:
   * Saudi Arabia -> SAR
   * UAE          -> AED
   * USA          -> USD
   * Egypt        -> EGP
   */
  document
    .querySelectorAll(
      '[data-testid="localization-currency-code"]'
    )
    .forEach((element) => {
      if (
        element.textContent.trim() !==
        selectedCurrency
      ) {
        element.textContent =
          selectedCurrency;
      }
    });

  /*
   * Also correct each country's currency inside
   * Shopify's country dropdown.
   */
  document
    .querySelectorAll(
      '.localization-form__list-item[data-value]'
    )
    .forEach((countryItem) => {
      const countryCode = (
        countryItem.dataset.value || ''
      ).toUpperCase();

      const currencyCode =
        hbaGetDisplayCurrencyForCountry(
          countryCode
        );

      if (!currencyCode) {
        return;
      }

      const currencyElement =
        countryItem.querySelector(
          '.localization-form__currency'
        );

      if (
        currencyElement &&
        currencyElement.textContent.trim() !==
          currencyCode
      ) {
        currencyElement.textContent =
          currencyCode;
      }
    });
}

function hbaStartLocalizationCurrencyLabels() {
  /*
   * Run immediately.
   */
  hbaUpdateLocalizationCurrencyLabels();

  /*
   * Horizon hydrates/rebuilds parts of the header after initial page load.
   * Shopify then writes the actual store currency (EGP) back into the DOM.
   *
   * Watch the header so whenever that happens we restore the customer's
   * DISPLAY currency.
   */
  const header =
    document.querySelector(
      '#header-component'
    ) ||
    document.querySelector(
      'header'
    );

  if (header) {
    let scheduled = false;

    const scheduleUpdate = () => {
      if (scheduled) {
        return;
      }

      scheduled = true;

      requestAnimationFrame(() => {
        scheduled = false;
        hbaUpdateLocalizationCurrencyLabels();
      });
    };

    const observer =
      new MutationObserver(
        scheduleUpdate
      );

    observer.observe(
      header,
      {
        subtree: true,
        childList: true,
        characterData: true,
      }
    );
  }

  /*
   * Theme-editor / Shopify section redraw support.
   */
  document.addEventListener(
    'shopify:section:load',
    () => {
      hbaUpdateLocalizationCurrencyLabels();
    }
  );

  /*
   * Extra delayed checks for late Horizon hydration.
   * These are lightweight and run only once.
   */
  setTimeout(
    hbaUpdateLocalizationCurrencyLabels,
    250
  );

  setTimeout(
    hbaUpdateLocalizationCurrencyLabels,
    750
  );

  setTimeout(
    hbaUpdateLocalizationCurrencyLabels,
    1500
  );

  setTimeout(
    hbaUpdateLocalizationCurrencyLabels,
    3000
  );
}

if (
  document.readyState === 'loading'
) {
  document.addEventListener(
    'DOMContentLoaded',
    hbaStartLocalizationCurrencyLabels,
    {
      once: true,
    }
  );
} else {
  hbaStartLocalizationCurrencyLabels();
}

/* -------------------------------------------------------------------------- */
/* HBA: ESSENTIAL UPSELL APP LOCAL-CURRENCY PRICES                            */
/* -------------------------------------------------------------------------- */

function hbaGetCountryForAppPrices() {
  const dataHook = document.querySelector(
    '[data-hba-currency-estimate][data-hba-country]'
  );

  const fromHook = (
    dataHook?.dataset.hbaCountry || ''
  ).toUpperCase();

  if (fromHook) {
    return fromHook;
  }

  if (
    typeof hbaGetSelectedCountryCode ===
    'function'
  ) {
    return hbaGetSelectedCountryCode();
  }

  return '';
}

function hbaFindEssentialUpsellRoots() {
  const roots = new Set();

  document
    .querySelectorAll(
      '.shopify-app-block, ' +
      '[id^="shopify-block-"], ' +
      '[class*="essential" i], ' +
      '[id*="essential" i]'
    )
    .forEach((element) => {
      const text = (
        element.textContent || ''
      ).toLowerCase();

      if (
        text.includes(
          'people also bought with'
        ) ||
        text.includes(
          'frequently bought'
        ) ||
        text.includes(
          'complete the look'
        )
      ) {
        roots.add(
          element.closest(
            '.shopify-app-block'
          ) || element
        );
      }
    });

  return [...roots];
}

function hbaParseEgpText(text) {
  if (!text) {
    return null;
  }

  const normalized = text
    .replace(/\u00a0/g, ' ')
    .replace(/,/g, '')
    .trim();

  const hasEgpMarker =
    /(?:\bEGP\b|\bLE\b|E£|£E|ج\.?\s?م\.?).*?\d|\d.*?(?:\bEGP\b|\bLE\b|E£|£E|ج\.?\s?م\.?)/i.test(
      normalized
    );

  if (!hasEgpMarker) {
    return null;
  }

  const numberMatch =
    normalized.match(
      /-?\d+(?:\.\d+)?/
    );

  if (!numberMatch) {
    return null;
  }

  const amount =
    Number(numberMatch[0]);

  return (
    Number.isFinite(amount) &&
    amount > 0
  )
    ? amount
    : null;
}

function hbaGetLeafPriceElements(
  root
) {
  return [
    ...root.querySelectorAll('*'),
  ].filter((element) => {
    if (
      element.children.length !== 0
    ) {
      return false;
    }

    return (
      hbaParseEgpText(
        element.textContent
      ) !== null
    );
  });
}

async function hbaConvertEssentialUpsellPrices() {
  const country =
    hbaGetCountryForAppPrices();

  /*
   * Egypt remains completely unchanged.
   */
  if (
    !country ||
    country ===
      HBA_DOMESTIC_COUNTRY
  ) {
    return;
  }

  const currencyCode =
    HBA_COUNTRY_CURRENCY[country];

  if (
    !currencyCode ||
    currencyCode ===
      HBA_BASE_CURRENCY
  ) {
    return;
  }

  const roots =
    hbaFindEssentialUpsellRoots();

  if (!roots.length) {
    return;
  }

  const rates =
    await hbaGetRates();

  /*
   * Safe fallback:
   * if exchange-rate data is unavailable,
   * leave the original EGP price visible.
   */
  if (!rates) {
    return;
  }

  const rate = Number(
    rates[
      currencyCode.toLowerCase()
    ]
  );

  if (
    !Number.isFinite(rate) ||
    rate <= 0
  ) {
    return;
  }

  for (const root of roots) {
    const priceElements =
      hbaGetLeafPriceElements(root);

    for (
      const priceElement of
      priceElements
    ) {
      const egpAmount =
        hbaParseEgpText(
          priceElement.textContent
        );

      if (!egpAmount) {
        continue;
      }

      const signature =
        `${country}|` +
        `${currencyCode}|` +
        `${egpAmount}`;

      if (
        priceElement.dataset
          .hbaUpsellCurrencySignature ===
        signature
      ) {
        continue;
      }

      const convertedAmount =
        egpAmount * rate;

      priceElement.textContent =
        hbaFormatCurrency(
          convertedAmount,
          currencyCode
        );

      priceElement.dataset
        .hbaUpsellCurrencySignature =
        signature;

      priceElement.dataset
        .hbaUpsellOriginalEgp =
        String(egpAmount);
    }
  }
}

function hbaStartEssentialUpsellCurrency() {
  let scheduled = false;

  const scheduleConversion =
    () => {
      if (scheduled) {
        return;
      }

      scheduled = true;

      requestAnimationFrame(
        () => {
          scheduled = false;

          hbaConvertEssentialUpsellPrices();
        }
      );
    };

  /*
   * Initial conversion.
   */
  scheduleConversion();

  /*
   * Essential Upsell can redraw itself after:
   * - page load
   * - variant change
   * - recommendation change
   * - carousel navigation
   * - add-to-cart interaction
   *
   * Watch for those dynamic updates.
   */
  const observer =
    new MutationObserver(
      scheduleConversion
    );

  observer.observe(
    document.body,
    {
      subtree: true,
      childList: true,
      characterData: true,
    }
  );

  document.addEventListener(
    'shopify:section:load',
    scheduleConversion
  );
}

if (
  document.readyState ===
  'loading'
) {
  document.addEventListener(
    'DOMContentLoaded',
    hbaStartEssentialUpsellCurrency,
    {
      once: true,
    }
  );
} else {
  hbaStartEssentialUpsellCurrency();
}

/* -------------------------------------------------------------------------- */
/* HBA: CART CURRENCY REPAIR AFTER HORIZON CART MORPHS                        */
/* -------------------------------------------------------------------------- */

/*
 * Horizon can dynamically replace/morph cart HTML after:
 *
 * - cart additions
 * - quantity changes
 * - discount changes
 * - localization changes
 *
 * When that happens, Shopify can restore the real EGP text even though
 * our hidden conversion hook still remembers that it was converted.
 *
 * This repair watches ONLY the cart drawer and full cart.
 * If an international cart visibly falls back to EGP, it re-runs the
 * existing HBA conversion.
 */

function hbaCartVisiblePriceText(hook) {
  if (!(hook instanceof HTMLElement)) {
    return '';
  }

  /*
   * Cart total.
   */
  const totalContainer =
    hook.closest('.cart__total-container');

  if (totalContainer) {
    const cartTotal =
      totalContainer.querySelector(
        '[ref="cartTotal"]'
      );

    return (
      cartTotal?.textContent || ''
    ).trim();
  }

  /*
   * Individual cart product / line total.
   */
  const priceContainer =
    hook.closest('[ref="priceContainer"]');

  if (!priceContainer) {
    return '';
  }

  const priceNode =
    priceContainer.querySelector('.price');

  return (
    priceNode?.textContent || ''
  ).trim();
}


function hbaCartPriceLooksLikeEgp(text) {
  if (!text) {
    return false;
  }

  const normalized =
    text
      .replace(/\u00a0/g, ' ')
      .trim();

  return (
    /\bEGP\b/i.test(normalized) ||
    /\bLE\b/i.test(normalized) ||
    /E£/i.test(normalized) ||
    /£E/i.test(normalized) ||
    /ج\.?\s?م\.?/i.test(normalized)
  );
}


function hbaRepairCartCurrency() {
  const hooks =
    document.querySelectorAll(
      'cart-drawer-component ' +
        '[data-hba-currency-estimate], ' +
      '.cart-page ' +
        '[data-hba-currency-estimate]'
    );

  hooks.forEach((hook) => {
    if (!(hook instanceof HTMLElement)) {
      return;
    }

    const country = (
      hook.dataset.hbaCountry || ''
    ).toUpperCase();

    /*
     * Never touch Egypt.
     */
    if (
      !country ||
      country === HBA_DOMESTIC_COUNTRY
    ) {
      return;
    }

    const visiblePrice =
      hbaCartVisiblePriceText(hook);

    /*
     * If it is already showing USD/SAR/AED/etc.,
     * do absolutely nothing.
     *
     * This prevents mutation loops.
     */
    if (
      !hbaCartPriceLooksLikeEgp(
        visiblePrice
      )
    ) {
      return;
    }

    /*
     * Horizon restored EGP but the previous
     * conversion signature may still exist.
     *
     * Clear ONLY that cached signature and reuse
     * our existing conversion function.
     */
    delete hook.dataset
      .hbaRenderedSignature;

    hbaRenderLocalPrice(hook);
  });
}


/*
 * Throttle repairs so multiple Horizon DOM changes
 * in the same frame only cause one scan.
 */
let hbaCartCurrencyRepairScheduled =
  false;

function hbaScheduleCartCurrencyRepair() {
  if (hbaCartCurrencyRepairScheduled) {
    return;
  }

  hbaCartCurrencyRepairScheduled =
    true;

  requestAnimationFrame(() => {
    hbaCartCurrencyRepairScheduled =
      false;

    hbaRepairCartCurrency();
  });
}


function hbaStartCartCurrencyRepair() {
  /*
   * Observe only the cart interfaces.
   * We intentionally do NOT observe the entire website.
   */
  const cartRoots =
    document.querySelectorAll(
      'cart-drawer-component, .cart-page'
    );

  const observer =
    new MutationObserver(() => {
      hbaScheduleCartCurrencyRepair();
    });

  cartRoots.forEach((root) => {
    observer.observe(
      root,
      {
        subtree: true,
        childList: true,
        characterData: true,
      }
    );
  });

  /*
   * Horizon's official theme events.
   */
  document.addEventListener(
    ThemeEvents.cartUpdate,
    hbaScheduleCartCurrencyRepair
  );

  document.addEventListener(
    ThemeEvents.discountUpdate,
    hbaScheduleCartCurrencyRepair
  );

  /*
   * Initial + delayed repairs catch the cart's
   * first hydration/morph after page load.
   */
  hbaScheduleCartCurrencyRepair();

  setTimeout(
    hbaScheduleCartCurrencyRepair,
    150
  );

  setTimeout(
    hbaScheduleCartCurrencyRepair,
    750
  );

  setTimeout(
    hbaScheduleCartCurrencyRepair,
    1500
  );
}


if (
  document.readyState === 'loading'
) {
  document.addEventListener(
    'DOMContentLoaded',
    hbaStartCartCurrencyRepair,
    {
      once: true,
    }
  );
} else {
  hbaStartCartCurrencyRepair();
}